import * as THREE from 'three';
import { NAVIGATION_MARKER_CONFIG } from '../utils/Constants.js';

/**
 * Manages navigation connections between images
 */
export class NavigationManager {
    constructor(scene, camera, raycastHelper, sphereManager = null) {
        this.scene = scene;
        this.camera = camera;
        this.raycastHelper = raycastHelper;
        this.sphereManager = sphereManager;

        this.connections = [];
        this.markerTexture = null;

        // Connection creation state
        this.isCreating = false;
        this.startImage = null;
        this.endImage = null;
        this.startPoint = null;
        this.endPoint = null;

        // UI components (will be injected)
        this.crosshairOverlay = null;
        this.placementControls = null;

        // Callbacks
        this.onConnectionCreated = null;
        this.onNavigationTriggered = null;

        // Load marker texture
        this.loadMarkerTexture();
    }

    /**
     * Set UI components for crosshair and placement controls
     * @param {CrosshairOverlay} crosshairOverlay - Crosshair overlay component
     * @param {PlacementControls} placementControls - Placement controls component
     */
    setUIComponents(crosshairOverlay, placementControls) {
        this.crosshairOverlay = crosshairOverlay;
        this.placementControls = placementControls;
    }

    /**
     * Load the navigation marker texture
     */
    loadMarkerTexture() {
        const loader = new THREE.TextureLoader();
        // Use path relative to public folder (Vite copies public/* to dist root)
        this.markerTexture = loader.load('/assets/hotspots.png');
    }

    /**
     * Start creating a navigation connection with crosshair
     * @param {Object} sourceImage - Source image data
     * @param {Object} destinationImage - Destination image data
     */
    startCreation(sourceImage, destinationImage, raycastTargets, onCursorChange) {
        this.isCreating = true;
        this.startImage = sourceImage;
        this.endImage = destinationImage;
        this.startPoint = null;
        this.endPoint = null;
        
        // Change cursor
        if (onCursorChange) {
            onCursorChange('grab'); // Keep normal cursor since we're using crosshair
        }
        
        // Show crosshair
        if (this.crosshairOverlay) {
            this.crosshairOverlay.show();
        }
        
        // Show placement controls
        if (this.placementControls) {
            this.placementControls.show(
                () => this.confirmPlacement(raycastTargets, onCursorChange),
                () => this.cancelCreation(onCursorChange)
            );
        }
        
        console.log('🚀 Navigation creation started with crosshair');
    }

    /**
     * Confirm placement at crosshair position (center of screen)
     */
    confirmPlacement(raycastTargets, onCursorChange) {
        if (!this.isCreating) return;
        
        // Create a synthetic mouse event at the center of the screen
        const centerEvent = {
            clientX: window.innerWidth / 2,
            clientY: window.innerHeight / 2
        };
        
        // Perform raycast at center of screen
        const intersection = this.raycastHelper.raycast(centerEvent, raycastTargets);
        
        if (!intersection) {
            console.log('❌ No intersection found at center of screen');
            return;
        }
        
        const point = intersection.point;
        
        if (!this.startPoint) {
            // Place start point
            this.startPoint = point.clone();
            console.log('🎯 Start point placed');
            
            // Hide UI components temporarily during transition
            if (this.crosshairOverlay) {
                this.crosshairOverlay.hide();
            }
            if (this.placementControls) {
                this.placementControls.hide();
            }
            
            // Trigger callback to transition to destination image
            if (this.onNavigationTriggered) {
                this.onNavigationTriggered(this.endImage, true); // true = is creating connection
            }
        } else {
            // Place end point
            this.endPoint = point.clone();
            console.log('🎯 End point placed');
            
            this.createConnection();
            this.finishCreation(onCursorChange);
        }
    }

    /**
     * Create the navigation connection
     */
    createConnection() {
        if (!this.startPoint || !this.endPoint || !this.startImage || !this.endImage) {
            console.warn('❌ Cannot create connection: missing data');
            return;
        }

        // Convert 3D positions to yaw/pitch for saving
        const startYawPitch = this.positionToYawPitch(this.startPoint);
        const endYawPitch = this.positionToYawPitch(this.endPoint);

        // Create connection data
        const connection = {
            id: Date.now(),
            startImage: this.startImage,
            endImage: this.endImage,
            startPoint: this.startPoint.clone(),
            endPoint: this.endPoint.clone(),
            startYaw: startYawPitch.yaw,
            startPitch: startYawPitch.pitch,
            endYaw: endYawPitch.yaw,
            endPitch: endYawPitch.pitch,
            startMarker: null,
            endMarker: null
        };

        console.log("createConnection", connection);
        
        // Create markers
        connection.startMarker = this.createMarker(connection.startPoint, connection);
        connection.endMarker = this.createMarker(connection.endPoint, connection);
        
        // Add to connections array
        this.connections.push(connection);
        
        // Trigger callback
        if (this.onConnectionCreated) {
            this.onConnectionCreated(connection);
        }
        
        // Trigger callback to return to original image
        if (this.onNavigationTriggered) {
            this.onNavigationTriggered(this.startImage, false); // false = not creating connection
        }
        
        console.log('✅ Navigation connection created');
    }

    /**
     * Remove a navigation connection between two images
     * @param {number} fromImageId - Source image ID
     * @param {number} toImageId - Destination image ID
     */
    removeConnection(fromImageId, toImageId) {
        const initialLength = this.connections.length;

        // Find and remove connections (bidirectional check)
        this.connections = this.connections.filter(conn => {
            const isMatch =
                (conn.startImage.id === fromImageId && conn.endImage.id === toImageId) ||
                (conn.startImage.id === toImageId && conn.endImage.id === fromImageId);

            // If we're removing this connection, dispose of its markers
            if (isMatch) {
                if (conn.startMarker) {
                    if (conn.startMarker.geometry) conn.startMarker.geometry.dispose();
                    if (conn.startMarker.material) conn.startMarker.material.dispose();
                    if (conn.startMarker.parent) conn.startMarker.parent.remove(conn.startMarker);
                }
                if (conn.endMarker) {
                    if (conn.endMarker.geometry) conn.endMarker.geometry.dispose();
                    if (conn.endMarker.material) conn.endMarker.material.dispose();
                    if (conn.endMarker.parent) conn.endMarker.parent.remove(conn.endMarker);
                }
            }

            return !isMatch;
        });

        if (this.connections.length < initialLength) {
            console.log(`[NavigationManager] Connection removed between images ${fromImageId} and ${toImageId}`);
        }
    }

    /**
     * Create a navigation marker
     * @param {THREE.Vector3} position - World position
     * @param {Object} connection - Associated connection
     * @returns {THREE.Mesh}
     */
    createMarker(position, connection) {
        const geometry = new THREE.PlaneGeometry(
            NAVIGATION_MARKER_CONFIG.SIZE,
            NAVIGATION_MARKER_CONFIG.SIZE
        );

        const material = new THREE.MeshBasicMaterial({
            map: this.markerTexture,
            transparent: true,
            opacity: NAVIGATION_MARKER_CONFIG.OPACITY,
            side: THREE.DoubleSide
        });

        const marker = new THREE.Mesh(geometry, material);

        // Calculate world position with scale
        const worldPosition = position.clone();
        worldPosition.multiplyScalar(NAVIGATION_MARKER_CONFIG.POSITION_SCALE);

        // Add to scene (not sphere) to avoid rotation conflicts between images
        marker.position.copy(worldPosition);
        marker.userData.originalPosition = worldPosition.clone();
        marker.lookAt(this.camera.position);

        marker.userData.animationOffset = 0;
        marker.userData.animationSpeed = NAVIGATION_MARKER_CONFIG.ANIMATION_SPEED;
        marker.userData.isNavigationMarker = true;
        marker.userData.connection = connection;

        return marker;
    }

    /**
     * Animate all visible markers (call in animation loop)
     */
    animateMarkers() {
        this.connections.forEach(connection => {
            if (connection.startMarker && connection.startMarker.visible) {
                this.animateMarker(connection.startMarker);
            }
            if (connection.endMarker && connection.endMarker.visible) {
                this.animateMarker(connection.endMarker);
            }
        });
    }

    /**
     * Animate a single marker
     * @param {THREE.Mesh} marker - Marker mesh
     */
    animateMarker(marker) {
        if (!marker.userData.originalPosition) return;
        
        marker.userData.animationOffset += marker.userData.animationSpeed;
        
        const verticalOffset = Math.sin(marker.userData.animationOffset) * NAVIGATION_MARKER_CONFIG.ANIMATION_AMPLITUDE;
        
        marker.position.copy(marker.userData.originalPosition);
        marker.position.y += verticalOffset;
    }

    /**
     * Handle click on navigation markers
     * @param {MouseEvent} event - Mouse event
     * @param {boolean} isPlacingHotspot - Whether hotspot placement is active
     * @returns {boolean} True if a marker was clicked
     */
    handleMarkerClick(event, isPlacingHotspot) {
        if (isPlacingHotspot || this.isCreating) return false;
        
        // Get all visible markers
        const markerObjects = [];
        this.connections.forEach(connection => {
            console.log('connection', connection)
            if (connection.startMarker && connection.startMarker.visible) {
                markerObjects.push(connection.startMarker);
            }
            if (connection.endMarker && connection.endMarker.visible) {
                markerObjects.push(connection.endMarker);
            }
        });
        
        if (markerObjects.length === 0) return false;
        
        // Perform raycast
        const intersections = this.raycastHelper.raycastAll(event, markerObjects);
        
        if (intersections.length > 0) {
            const clickedMarker = intersections[0].object;
            const connection = clickedMarker.userData.connection;

            console.log('[NavigationManager] 🖱️ Marker clicked!');
            console.log('  - Connection:', connection);
            console.log('  - Clicked marker is startMarker:', clickedMarker === connection.startMarker);
            console.log('  - Start image:', connection.startImage?.name);
            console.log('  - End image:', connection.endImage?.name);

            if (connection) {
                // Determine target image
                let targetImage;
                if (clickedMarker === connection.startMarker) {
                    targetImage = connection.endImage;
                    console.log('  ➡️ Going to endImage:', targetImage.name);
                } else {
                    targetImage = connection.startImage;
                    console.log('  ⬅️ Going to startImage:', targetImage.name);
                }

                // Trigger navigation
                if (this.onNavigationTriggered && targetImage) {
                    this.onNavigationTriggered(targetImage, false); // false = not creating connection
                }

                return true;
            }
        }
        
        return false;
    }

    /**
     * Show markers for a specific image (and hide all others)
     * @param {Object} imageData - Image data
     */
    showForImage(imageData) {
        console.log(`[NavigationManager] showForImage called for: ${imageData.name} (id: ${imageData.id})`);

        this.connections.forEach(connection => {
            console.log("connection test", connection);
            console.log("imageData", imageData);
            const startMatch = connection.startImage.id === imageData.id;
            const endMatch = connection.endImage.id === imageData.id;

            console.log(`  Connection ${connection.id}: from ${connection.startImage.name} (${connection.startImage.id}) to ${connection.endImage.name} (${connection.endImage.id})`);
            console.log(`    - Start match: ${startMatch}, End match: ${endMatch}`);

            // Handle startMarker visibility
            if (connection.startMarker) {
                if (startMatch) {
                    console.log(`    ✅ Showing startMarker for connection ${connection.id}`);
                    connection.startMarker.visible = true;
                    if (!this.scene.children.includes(connection.startMarker)) {
                        this.scene.add(connection.startMarker);
                    }
                } else {
                    // Hide markers from other images
                    connection.startMarker.visible = false;
                }
            }

            // Handle endMarker visibility
            if (connection.endMarker) {
                if (endMatch) {
                    console.log(`    ✅ Showing endMarker for connection ${connection.id}`);
                    connection.endMarker.visible = true;
                    if (!this.scene.children.includes(connection.endMarker)) {
                        this.scene.add(connection.endMarker);
                    }
                } else {
                    // Hide markers from other images
                    connection.endMarker.visible = false;
                }
            }
        });
    }

    /**
     * Hide all markers (typically called during transitions)
     * @param {Object} imageData - Image data (optional, for backwards compatibility)
     */
    hideForImage(imageData) {
        if (imageData) {
            console.log(`[NavigationManager] hideForImage called for: ${imageData.name} (id: ${imageData.id})`);
        } else {
            console.log(`[NavigationManager] hideForImage called - hiding all markers`);
        }

        // Hide ALL markers during transitions to prevent ghost markers
        this.connections.forEach(connection => {
            if (connection.startMarker) {
                connection.startMarker.visible = false;
            }
            if (connection.endMarker) {
                connection.endMarker.visible = false;
            }
        });
    }

    /**
     * Finish connection creation
     */
    finishCreation(onCursorChange) {
        this.cleanup();
        
        // Hide UI components
        if (this.crosshairOverlay) {
            this.crosshairOverlay.hide();
        }
        
        if (this.placementControls) {
            this.placementControls.hide();
        }
        
        if (onCursorChange) {
            onCursorChange('grab');
        }
    }

    /**
     * Cancel connection creation
     */
    cancelCreation(onCursorChange) {
        this.cleanup();
        
        // Hide UI components
        if (this.crosshairOverlay) {
            this.crosshairOverlay.hide();
        }
        
        if (this.placementControls) {
            this.placementControls.hide();
        }
        
        if (onCursorChange) {
            onCursorChange('grab');
        }
    }

    /**
     * Continue connection creation after transition (for placing end point)
     * @param {Object} raycastTargets - Objects to raycast against
     * @param {Function} onCursorChange - Cursor change callback
     */
    continueCreation(raycastTargets, onCursorChange) {
        if (!this.isCreating || !this.startPoint) return;
        
        // Show crosshair and controls again for end point placement
        if (this.crosshairOverlay) {
            this.crosshairOverlay.show();
        }
        
        if (this.placementControls) {
            this.placementControls.show(
                () => this.confirmPlacement(raycastTargets, onCursorChange),
                () => this.cancelCreation(onCursorChange)
            );
        }
        
        console.log('🎯 Ready to place end point');
    }

    /**
     * Cleanup creation state
     */
    cleanup() {
        this.isCreating = false;
        this.startImage = null;
        this.endImage = null;
        this.startPoint = null;
        this.endPoint = null;
    }

    /**
     * Check if in creation mode
     * @returns {boolean}
     */
    isInCreationMode() {
        return this.isCreating;
    }

    /**
     * Get all connections
     * @returns {Array}
     */
    getAllConnections() {
        return this.connections;
    }

    /**
     * Load connections from API data
     * @param {Array} connectionsData - Array of connection objects from API (with from/to _ids and pitch/yaw)
     * @param {Function} getImageByPictureId - Function to map picture id to image data object
     */
    loadConnectionsFromAPI(connectionsData, getImageByPictureId) {
        if (!connectionsData || !Array.isArray(connectionsData)) {
            console.warn('[NavigationManager] No connections data to load');
            return;
        }

        console.log(`[NavigationManager] Loading ${connectionsData.length} connections from API...`);

        connectionsData.forEach((connData, index) => {
            try {
                console.log(`[NavigationManager] 🔍 Processing connection ${index}: from=${connData.from}, to=${connData.to}`);

                // Map picture IDs to image data objects
                const sourceImage = getImageByPictureId(connData.from);
                const destinationImage = getImageByPictureId(connData.to);

                console.log(`[NavigationManager] 📸 Resolved images:`);
                console.log(`  - Source (from ${connData.from}):`, sourceImage ? sourceImage.name : 'NOT FOUND');
                console.log(`  - Destination (to ${connData.to}):`, destinationImage ? destinationImage.name : 'NOT FOUND');

                if (!sourceImage || !destinationImage) {
                    console.warn(`[NavigationManager] ❌ Skipping connection ${index}: Could not find images for from=${connData.from}, to=${connData.to}`);
                    return;
                }

                // Extract pitch/yaw from startPosition (API format)
                const pitch = connData.startPosition?.pitch ?? connData.pitch ?? 0;
                const yaw = connData.startPosition?.yaw ?? connData.yaw ?? 0;

                console.log(`[NavigationManager] Connection ${index} - from: ${connData.from}, to: ${connData.to}, pitch: ${pitch}, yaw: ${yaw}`);

                const markerPosition = this.pitchYawToPosition(pitch, yaw);

                // Store yaw/pitch for later use
                const startYaw = yaw;
                const startPitch = pitch;
                const endYaw = connData.endPosition?.yaw ?? 0;
                const endPitch = connData.endPosition?.pitch ?? 0;

                // Create connection object (one marker per connection)
                const connection = {
                    id: connData.id || connData._id || `api-${index}`,
                    startImage: sourceImage,  // Image where the marker appears
                    endImage: destinationImage,  // Image to navigate to when clicked
                    startPoint: markerPosition,
                    endPoint: null,  // Not needed for API connections
                    startYaw: startYaw,
                    startPitch: startPitch,
                    endYaw: endYaw,
                    endPitch: endPitch,
                    startMarker: null,
                    endMarker: null  // Not used for API connections
                };

                console.log("fromAPIConnection", connection);

                // Create the marker in the source image
                connection.startMarker = this.createMarker(connection.startPoint, connection);

                // Hide marker initially (will be shown when displaying the source image)
                connection.startMarker.visible = false;

                // Add to scene
                this.scene.add(connection.startMarker);

                // Add to connections array
                this.connections.push(connection);

                console.log(`[NavigationManager] ✅ Loaded connection: ${sourceImage.name} -> ${destinationImage.name} (pitch: ${pitch}, yaw: ${yaw})`);
            } catch (error) {
                console.error(`[NavigationManager] Error loading connection ${index}:`, error);
            }
        });

        console.log(`[NavigationManager] Successfully loaded ${this.connections.length} connections`);
    }

    /**
     * Convert pitch and yaw angles to 3D position on sphere
     * Compatible with Photo Sphere Viewer's longitude/latitude system
     * @param {number} pitch - Pitch angle in degrees (latitude, vertical rotation)
     * @param {number} yaw - Yaw angle in degrees (longitude, horizontal rotation)
     * @param {number} radius - Sphere radius (default: 500)
     * @returns {THREE.Vector3} 3D position on sphere
     */
    pitchYawToPosition(pitch, yaw, radius = 500) {
        // Convert degrees to radians (same as Photo Sphere Viewer)
        const latitude = pitch * (Math.PI / 180);
        const longitude = yaw * (Math.PI / 180);

        // Convert spherical coordinates to Cartesian (Three.js format)
        // Photo Sphere Viewer convention: longitude=0, latitude=0 is the center front
        // Adjusted for Three.js where camera looks along -Z axis
        const x = radius * Math.cos(latitude) * Math.sin(longitude); // Inverted X axis
        const y = radius * Math.sin(latitude); // Inverted Y axis
        const z = -radius * Math.cos(latitude) * Math.cos(longitude);

        return new THREE.Vector3(x, y, z);
    }

    /**
     * Convert 3D position on sphere to pitch and yaw angles
     * Inverse of pitchYawToPosition - converts Cartesian to spherical coordinates
     * @param {THREE.Vector3} position - 3D position on sphere
     * @returns {Object} Object with yaw and pitch in degrees
     */
    positionToYawPitch(position) {
        // Normalize the position vector (in case it's not exactly on the sphere surface)
        const normalized = position.clone().normalize();

        // Extract x, y, z components
        const x = normalized.x;
        const y = normalized.y;
        const z = normalized.z;

        // Calculate pitch (latitude) - vertical angle from horizontal plane
        // pitch = arcsin(y)
        const pitch = Math.asin(y);

        // Calculate yaw (longitude) - horizontal angle around Y axis
        // yaw = atan2(x, -z)
        const yaw = Math.atan2(x, -z);

        // Convert from radians to degrees
        const pitchDegrees = pitch * (180 / Math.PI);
        const yawDegrees = yaw * (180 / Math.PI);

        return {
            yaw: yawDegrees,
            pitch: pitchDegrees
        };
    }

    /**
     * Cleanup and dispose all resources
     */
    dispose() {
        console.log('[NavigationManager] Disposing...');

        // Remove all connections from scene and dispose resources
        this.connections.forEach(connection => {
            // Remove and dispose start marker
            if (connection.startMarker) {
                if (connection.startMarker.parent) {
                    connection.startMarker.parent.remove(connection.startMarker);
                }
                if (connection.startMarker.geometry) {
                    connection.startMarker.geometry.dispose();
                }
                if (connection.startMarker.material) {
                    if (connection.startMarker.material.map) {
                        connection.startMarker.material.map.dispose();
                    }
                    connection.startMarker.material.dispose();
                }
            }

            // Remove and dispose end marker
            if (connection.endMarker) {
                if (connection.endMarker.parent) {
                    connection.endMarker.parent.remove(connection.endMarker);
                }
                if (connection.endMarker.geometry) {
                    connection.endMarker.geometry.dispose();
                }
                if (connection.endMarker.material) {
                    if (connection.endMarker.material.map) {
                        connection.endMarker.material.map.dispose();
                    }
                    connection.endMarker.material.dispose();
                }
            }
        });

        this.connections = [];

        // Dispose placement preview mesh if exists
        if (this.previewMesh) {
            if (this.previewMesh.parent) {
                this.previewMesh.parent.remove(this.previewMesh);
            }
            if (this.previewMesh.geometry) {
                this.previewMesh.geometry.dispose();
            }
            if (this.previewMesh.material) {
                if (this.previewMesh.material.map) {
                    this.previewMesh.material.map.dispose();
                }
                this.previewMesh.material.dispose();
            }
            this.previewMesh = null;
        }

        // Dispose marker texture
        if (this.markerTexture) {
            this.markerTexture.dispose();
            this.markerTexture = null;
        }

        // Clear callbacks
        this.onConnectionCreated = null;

        console.log('[NavigationManager] Disposed successfully');
    }
}

